import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, real, numeric, unique, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

import { tenants, companies, projects, sites, areas, gates, users } from './common';
import { workers, deployments } from './workers';

export const shiftDefinitions = pgTable("shift_definitions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull(),
  name: text("name").notNull(),
  description: text("description"),

  shiftType: text("shift_type").notNull().default("regular"),
  status: text("status").notNull().default("active"),

  // HEAD time columns
  plannedStartTime: text("planned_start_time"),
  plannedEndTime: text("planned_end_time"),
  plannedDurationMinutes: integer("planned_duration_minutes"),
  // Ours time columns
  startTime: text("start_time").notNull(),
  endTime: text("end_time").notNull(),
  durationMinutes: integer("duration_minutes").notNull(),

  // HEAD break structure
  breakStructure: jsonb("break_structure").$type<{ breakStart: string; breakEnd: string; durationMinutes: number; paid: boolean }[]>().default([]),
  // Ours break column
  breakMinutes: integer("break_minutes").default(0),

  // HEAD grace/threshold columns
  graceMinutesLate: integer("grace_minutes_late").default(0),
  graceMinutesEarly: integer("grace_minutes_early").default(0),
  lateThresholdMinutes: integer("late_threshold_minutes").default(15),
  earlyExitThresholdMinutes: integer("early_exit_threshold_minutes").default(15),
  overtimeThresholdMinutes: integer("overtime_threshold_minutes").default(0),

  // Scoping - HEAD FK references
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  projectId: text("project_id").references(() => projects.id),
  siteId: text("site_id").references(() => sites.id),
  areaId: text("area_id").references(() => areas.id),
  companyId: text("company_id").references(() => companies.id),

  divisionId: text("division_id"),

  // HEAD crew/role/supervisor
  crewId: text("crew_id"),
  roleId: text("role_id"),
  supervisorId: text("supervisor_id").references(() => workers.id),

  // HEAD scheduling columns
  repeatingPattern: text("repeating_pattern"),
  crossMidnight: boolean("cross_midnight").notNull().default(false),
  standbyShift: boolean("standby_shift").notNull().default(false),
  dayNightIndicator: text("day_night_indicator").default("day"),

  // HEAD attendance config
  requiresCheckIn: boolean("requires_check_in").notNull().default(true),
  requiresCheckOut: boolean("requires_check_out").notNull().default(true),
  gateBasedAttendance: boolean("gate_based_attendance").notNull().default(false),
  supervisorConfirmation: boolean("supervisor_confirmation").notNull().default(false),
  inAreaPresenceRequired: boolean("in_area_presence_required").notNull().default(false),

  // Ours columns
  crossesMidnight: boolean("crosses_midnight").notNull().default(false),
  applicableDays: text("applicable_days").array().default([]),
  color: text("color"),
  isTemplate: boolean("is_template").notNull().default(false),
  templateSourceId: text("template_source_id"),
  minWorkers: integer("min_workers"),
  maxWorkers: integer("max_workers"),
  requiredRoles: text("required_roles").array().default([]),
  requiredCompetencies: text("required_competencies").array().default([]),

  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertShiftDefinitionSchema = createInsertSchema(shiftDefinitions).omit({ id: true, tenantId: true });
export type InsertShiftDefinition = z.infer<typeof insertShiftDefinitionSchema>;
export type ShiftDefinition = typeof shiftDefinitions.$inferSelect;

// ─── Shift Rosters (Time & Attendance Module) ────────────────────────
export const shiftRosters = pgTable("shift_rosters", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  rosterType: text("roster_type").notNull().default("daily"),
  projectId: text("project_id").references(() => projects.id),
  siteId: text("site_id").references(() => sites.id),
  companyId: text("company_id").references(() => companies.id),
  status: text("status").notNull().default("draft"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertShiftRosterSchema = createInsertSchema(shiftRosters).omit({ id: true, tenantId: true });
export type InsertShiftRoster = z.infer<typeof insertShiftRosterSchema>;
export type ShiftRoster = typeof shiftRosters.$inferSelect;

// ─── Rosters ─────────────────────────────────────────────────────────
export const rosters = pgTable("rosters", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  code: text("code").notNull(),
  description: text("description"),

  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  companyId: text("company_id"),

  periodStart: text("period_start").notNull(),
  periodEnd: text("period_end").notNull(),
  timeZone: text("time_zone").default("UTC"),

  status: text("status").notNull().default("draft"),
  publishedAt: text("published_at"),
  publishedBy: text("published_by"),
  frozenAt: text("frozen_at"),
  frozenBy: text("frozen_by"),

  totalShifts: integer("total_shifts").default(0),
  totalAssignments: integer("total_assignments").default(0),
  unfilledCount: integer("unfilled_count").default(0),

  notes: text("notes"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertRosterSchema = createInsertSchema(rosters).omit({ id: true, tenantId: true });
export type InsertRoster = z.infer<typeof insertRosterSchema>;
export type Roster = typeof rosters.$inferSelect;

// ─── Shift Assignments ──────────────────────────────────────────────
export const shiftAssignments = pgTable("shift_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  shiftDefinitionId: text("shift_definition_id").notNull().references(() => shiftDefinitions.id),
  rosterId: text("roster_id").notNull().references(() => shiftRosters.id),

  assignmentDate: text("assignment_date").notNull(),
  effectiveStartTime: text("effective_start_time"),
  effectiveEndTime: text("effective_end_time"),

  companyId: text("company_id").references(() => companies.id),
  projectId: text("project_id").references(() => projects.id),
  siteId: text("site_id").references(() => sites.id),
  areaId: text("area_id").references(() => areas.id),

  // HEAD columns
  crewId: text("crew_id"),
  supervisorId: text("supervisor_id").references(() => workers.id),
  deploymentId: text("deployment_id").references(() => deployments.id),
  scheduledStart: text("scheduled_start"),
  scheduledEnd: text("scheduled_end"),
  expectedHours: numeric("expected_hours"),
  assignmentSource: text("assignment_source").notNull().default("manual"),
  criticalRole: boolean("critical_role").notNull().default(false),

  status: text("status").notNull().default("assigned"),
  confirmationStatus: text("confirmation_status").default("pending"),
  confirmedAt: text("confirmed_at"),
  confirmedBy: text("confirmed_by"),
  replacedByWorkerId: text("replaced_by_worker_id"),
  replacementReason: text("replacement_reason"),
  cancellationReason: text("cancellation_reason"),
  cancelledAt: text("cancelled_at"),
  cancelledBy: text("cancelled_by"),
  notes: text("notes"),
  assignedBy: text("assigned_by"),
  gracePeriodMinutes: integer("grace_period_minutes").default(15),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertShiftAssignmentSchema = createInsertSchema(shiftAssignments).omit({ id: true, tenantId: true });
export type InsertShiftAssignment = z.infer<typeof insertShiftAssignmentSchema>;
export type ShiftAssignment = typeof shiftAssignments.$inferSelect;
export const attendanceRecords = pgTable("attendance_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  attendanceDate: text("attendance_date").notNull(),
  shiftAssignmentId: text("shift_assignment_id").references(() => shiftAssignments.id),
  attendanceType: text("attendance_type").notNull().default("regular"),
  companyId: text("company_id").references(() => companies.id),
  projectId: text("project_id").references(() => projects.id),
  siteId: text("site_id").references(() => sites.id),
  areaId: text("area_id").references(() => areas.id),
  crewId: text("crew_id"),
  supervisorId: text("supervisor_id").references(() => workers.id),
  deploymentId: text("deployment_id").references(() => deployments.id),
  plannedStart: text("planned_start"),
  plannedEnd: text("planned_end"),
  actualFirstCheckIn: text("actual_first_check_in"),
  actualLastCheckOut: text("actual_last_check_out"),
  attendanceWindowStart: text("attendance_window_start"),
  attendanceWindowEnd: text("attendance_window_end"),
  status: text("status").notNull().default("scheduled"),
  captureMethod: text("capture_method"),
  verified: boolean("verified").notNull().default(false),
  exception: boolean("exception").notNull().default(false),
  correctionRequired: boolean("correction_required").notNull().default(false),
  locked: boolean("locked").notNull().default(false),
  attendanceFlag: text("attendance_flag").default("on_time"),
  lateMinutes: integer("late_minutes").default(0),
  earlyExitMinutes: integer("early_exit_minutes").default(0),
  totalMinutesWorked: integer("total_minutes_worked"),
  isUnscheduled: boolean("is_unscheduled").notNull().default(false),
  captureDeviceId: text("capture_device_id"),
  captureOperator: text("capture_operator"),
  gateId: text("gate_id"),
  notes: text("notes"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxAttendanceRecordsTenantWorker: index("idx_attendance_records_tenant_worker").on(t.tenantId, t.workerId),
  idxAttendanceRecordsTenantStatus: index("idx_attendance_records_tenant_status").on(t.tenantId, t.status),
  idxAttendanceRecordsTenantCreatedAt: index("idx_attendance_records_tenant_created_at").on(t.tenantId, t.createdAt),
  idxAttendanceRecordsWorkerStatus: index("idx_attendance_records_worker_status").on(t.workerId, t.status),
}));
export const insertAttendanceRecordSchema = createInsertSchema(attendanceRecords).omit({ id: true, tenantId: true });
export type InsertAttendanceRecord = z.infer<typeof insertAttendanceRecordSchema>;
export type AttendanceRecord = typeof attendanceRecords.$inferSelect;
export const presenceEvents = pgTable("presence_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  companyId: text("company_id").references(() => companies.id),
  projectId: text("project_id").references(() => projects.id),
  siteId: text("site_id").references(() => sites.id),
  sourceAreaId: text("source_area_id").references(() => areas.id),
  targetAreaId: text("target_area_id").references(() => areas.id),
  gateId: text("gate_id").references(() => gates.id),
  checkpointId: text("checkpoint_id"),
  eventType: text("event_type").notNull(),
  eventTimestamp: text("event_timestamp").notNull(),
  captureSource: text("capture_source"),
  confidenceScore: numeric("confidence_score"),
  verificationStatus: text("verification_status").default("unverified"),
  linkedAccessEventId: text("linked_access_event_id"),
  linkedAttendanceRecordId: text("linked_attendance_record_id").references(() => attendanceRecords.id),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertPresenceEventSchema = createInsertSchema(presenceEvents).omit({ id: true, tenantId: true });
export type InsertPresenceEvent = z.infer<typeof insertPresenceEventSchema>;
export type PresenceEvent = typeof presenceEvents.$inferSelect;
export const timesheets = pgTable("timesheets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  periodStart: text("period_start").notNull(),
  periodEnd: text("period_end").notNull(),
  timesheetType: text("timesheet_type").notNull().default("weekly"),
  companyId: text("company_id").references(() => companies.id),
  projectId: text("project_id").references(() => projects.id),
  siteId: text("site_id").references(() => sites.id),
  areaId: text("area_id").references(() => areas.id),
  deploymentId: text("deployment_id").references(() => deployments.id),
  supervisorId: text("supervisor_id").references(() => workers.id),
  scheduledHours: numeric("scheduled_hours"),
  attendedHours: numeric("attended_hours"),
  workedHours: numeric("worked_hours"),
  overtimeHours: numeric("overtime_hours"),
  breakHours: numeric("break_hours"),
  unpaidHours: numeric("unpaid_hours"),
  excusedAbsenceHours: numeric("excused_absence_hours"),
  missingHours: numeric("missing_hours"),
  disputedHours: numeric("disputed_hours"),
  sourceBasis: text("source_basis").notNull().default("attendance-derived"),
  status: text("status").notNull().default("draft"),
  lateCount: integer("late_count").default(0),
  earlyExitCount: integer("early_exit_count").default(0),
  noShowCount: integer("no_show_count").default(0),
  missingCheckoutCount: integer("missing_checkout_count").default(0),
  manualEditCount: integer("manual_edit_count").default(0),
  overrideCount: integer("override_count").default(0),
  requiresReApproval: boolean("requires_re_approval").notNull().default(false),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxTimesheetsTenantWorker: index("idx_timesheets_tenant_worker").on(t.tenantId, t.workerId),
  idxTimesheetsTenantStatus: index("idx_timesheets_tenant_status").on(t.tenantId, t.status),
  idxTimesheetsTenantCreatedAt: index("idx_timesheets_tenant_created_at").on(t.tenantId, t.createdAt),
  idxTimesheetsWorkerStatus: index("idx_timesheets_worker_status").on(t.workerId, t.status),
}));
export const insertTimesheetSchema = createInsertSchema(timesheets).omit({ id: true, tenantId: true });
export type InsertTimesheet = z.infer<typeof insertTimesheetSchema>;
export type Timesheet = typeof timesheets.$inferSelect;
export const timesheetLineItems = pgTable("timesheet_line_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  timesheetId: text("timesheet_id").notNull().references(() => timesheets.id),
  lineDate: text("line_date").notNull(),
  shiftAssignmentId: text("shift_assignment_id").references(() => shiftAssignments.id),
  attendanceRecordId: text("attendance_record_id").references(() => attendanceRecords.id),
  scheduledHours: numeric("scheduled_hours"),
  actualHours: numeric("actual_hours"),
  overtimeHours: numeric("overtime_hours"),
  breakHours: numeric("break_hours"),
  timeType: text("time_type").notNull().default("regular"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertTimesheetLineItemSchema = createInsertSchema(timesheetLineItems).omit({ id: true, tenantId: true });
export type InsertTimesheetLineItem = z.infer<typeof insertTimesheetLineItemSchema>;
export type TimesheetLineItem = typeof timesheetLineItems.$inferSelect;
export const timeExceptions = pgTable("time_exceptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  attendanceRecordId: text("attendance_record_id").references(() => attendanceRecords.id),
  timesheetId: text("timesheet_id").references(() => timesheets.id),
  presenceEventId: text("presence_event_id").references(() => presenceEvents.id),
  exceptionType: text("exception_type").notNull(),
  severity: text("severity").notNull().default("medium"),
  status: text("status").notNull().default("open"),
  reasonCode: text("reason_code"),
  notes: text("notes"),
  proposedCorrection: jsonb("proposed_correction").$type<Record<string, any>>(),
  approvedCorrection: jsonb("approved_correction").$type<Record<string, any>>(),
  approverId: text("approver_id"),
  resolvedAt: text("resolved_at"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertTimeExceptionSchema = createInsertSchema(timeExceptions).omit({ id: true, tenantId: true });
export type InsertTimeException = z.infer<typeof insertTimeExceptionSchema>;
export type TimeException = typeof timeExceptions.$inferSelect;
export const timesheetApprovals = pgTable("timesheet_approvals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  timesheetId: text("timesheet_id").references(() => timesheets.id),
  batchId: text("batch_id"),
  approvalStage: text("approval_stage").notNull(),
  approverRole: text("approver_role"),
  approverUserId: text("approver_user_id"),
  status: text("status").notNull().default("pending"),
  comments: text("comments"),
  rejectionReason: text("rejection_reason"),
  nextStage: text("next_stage"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertTimesheetApprovalSchema = createInsertSchema(timesheetApprovals).omit({ id: true, tenantId: true });
export type InsertTimesheetApproval = z.infer<typeof insertTimesheetApprovalSchema>;
export type TimesheetApproval = typeof timesheetApprovals.$inferSelect;
export const timeAuditEvents = pgTable("time_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  eventType: text("event_type").notNull(),
  actor: text("actor"),
  actorRole: text("actor_role"),
  eventTimestamp: text("event_timestamp").notNull(),
  affectedEntityType: text("affected_entity_type").notNull(),
  affectedEntityId: text("affected_entity_id").notNull(),
  scopeContext: jsonb("scope_context").$type<Record<string, any>>().default({}),
  oldValue: jsonb("old_value").$type<Record<string, any>>(),
  newValue: jsonb("new_value").$type<Record<string, any>>(),
  reason: text("reason"),
  comments: text("comments"),
  captureSource: text("capture_source"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertTimeAuditEventSchema = createInsertSchema(timeAuditEvents).omit({ id: true, tenantId: true });
export type InsertTimeAuditEvent = z.infer<typeof insertTimeAuditEventSchema>;
export type TimeAuditEvent = typeof timeAuditEvents.$inferSelect;

export const timesheetCorrections = pgTable("timesheet_corrections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  timesheetId: text("timesheet_id").notNull().references(() => timesheets.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  correctionType: text("correction_type").notNull(),
  reason: text("reason").notNull(),
  status: text("status").notNull().default("pending"),
  attachmentDocumentId: text("attachment_document_id"),
  attachmentFileName: text("attachment_file_name"),
  reviewedBy: text("reviewed_by"),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
  reviewNote: text("review_note"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxTimesheetCorrectionsTimesheet: index("idx_timesheet_corrections_timesheet").on(t.timesheetId),
  idxTimesheetCorrectionsWorker: index("idx_timesheet_corrections_worker").on(t.workerId),
}));
export const insertTimesheetCorrectionSchema = createInsertSchema(timesheetCorrections).omit({ id: true, tenantId: true, reviewedAt: true, createdAt: true, updatedAt: true });
export type InsertTimesheetCorrection = z.infer<typeof insertTimesheetCorrectionSchema>;
export type TimesheetCorrection = typeof timesheetCorrections.$inferSelect;

// ─── Equipment & Assets Domain ──────────────────────────────────────
export const leaveRequests = pgTable("leave_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  companyId: text("company_id").references(() => companies.id),
  leaveType: text("leave_type").notNull(),
  startDate: text("start_date").notNull(),
  endDate: text("end_date").notNull(),
  daysRequested: real("days_requested").notNull().default(1),
  reason: text("reason"),
  status: text("status").notNull().default("pending"),
  reviewedBy: text("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
  reviewNote: text("review_note"),
  attachmentUrl: text("attachment_url"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertLeaveRequestSchema = createInsertSchema(leaveRequests).omit({ id: true, createdAt: true, updatedAt: true, reviewedAt: true });
export type InsertLeaveRequest = z.infer<typeof insertLeaveRequestSchema>;
export type LeaveRequest = typeof leaveRequests.$inferSelect;
export const leaveBalances = pgTable("leave_balances", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  companyId: text("company_id").references(() => companies.id),
  leaveType: text("leave_type").notNull(),
  year: integer("year").notNull(),
  entitledDays: real("entitled_days").notNull().default(0),
  usedDays: real("used_days").notNull().default(0),
  pendingDays: real("pending_days").notNull().default(0),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqWorkerTypeYear: unique("leave_balances_worker_type_year").on(t.workerId, t.leaveType, t.year),
}));
export const insertLeaveBalanceSchema = createInsertSchema(leaveBalances).omit({ id: true, updatedAt: true });
export type InsertLeaveBalance = z.infer<typeof insertLeaveBalanceSchema>;
export type LeaveBalance = typeof leaveBalances.$inferSelect;

// ─── Tenant Features Table ─────────────────────────────────────────────────
